import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Center,
  Text,
  Button,
  Grid,
  Flex,
  chakra,
} from '@chakra-ui/react';
import _ from 'lodash';
import ImageReel from '@rsc/ui-components/dept/atoms/ImageReel';
import Link from '@rsc/ui-components/tailwind/Link';
import BlueContainerSVG from './blue-container.svg';

export default function CommunityGrid({ block }) {
  const { headline, copy, images, cta } = block;
  const chunkedImages = _.chunk(images, 4);

  return (
    <Container my={16} px={{ base: 0, lg: '80px' }}>
      <Flex
        pos="relative"
        h={{ base: '43.75rem', lg: '55rem' }}
        flexDir={{ base: 'column', lg: 'row' }}
        alignItems="center"
        justify="space-between"
        overflowY="hidden"
      >
        <Center
          flexDir="column"
          textAlign="center"
          gap={{ base: 4, lg: 8 }}
          bgImage={BlueContainerSVG}
          bgRepeat="no-repeat"
          bgSize="100% 100%"
          bgPosition="center"
          w={{ base: '19.43rem', lg: '41.25rem' }}
          h={{ base: '20.12rem', lg: '37.5rem' }}
          pt="2rem"
          px={{ base: 4, lg: '3.5rem' }}
          zIndex="2"
          mr={{ base: 0, lg: '-10rem' }}
          pos="relative"
          top={{ base: '1rem', lg: 0 }}
        >
          <chakra.svg
            w={{ base: '34px', lg: '48px' }}
            h={{ base: '32px', lg: '46px' }}
            viewBox="0 0 48 46"
            fill="none"
          >
            <path
              d="M46.6047 11.7962L25.1047 0.393615C25.0116 0.347544 24.907 0.289954 24.814 0.255401C24.3023 0.0711163 23.7442 0.0711163 23.2326 0.255401C23.1279 0.289954 23.0349 0.336026 22.9419 0.393615L1.37208 11.7962C0.534875 12.1648 0 12.9826 0 13.8925V42.8251C0 44.0921 1.03487 45.1172 2.31394 45.1172H12.907C14.4302 44.979 16.2558 44.7371 18.1977 43.9769C20.9651 42.8943 23.814 40.6368 23.593 37.6997C23.407 35.3271 21.1977 33.5418 18.8954 32.8623C16.593 32.1942 14.1395 32.344 11.7442 32.1482C10.4767 32.0445 9.05814 31.7681 8.60465 30.3168C8.3372 29.4645 8.55814 28.5431 9.11628 27.852C13.2326 22.7496 22.9186 21.1141 28.9535 22.6575C34.7674 24.1433 39.6977 28.474 39.7093 34.7627C39.7093 37.9531 37.5116 42.8482 31.7907 44.2418C31.3954 44.3225 31.3256 44.5528 31.3256 44.7832C31.3256 44.9559 31.4651 45.1057 31.6395 45.1057H45.6861C46.9651 45.1057 48 44.0806 48 42.8136V13.8809C48 12.971 47.4651 12.1533 46.6279 11.7847L46.6047 11.7962Z"
              fill="#FFEB00"
            />
          </chakra.svg>

          {headline && (
            <Text
              as="h2"
              color="typography.lighter"
              textStyle={{ base: 'h2', lg: 'h1' }}
              sx={{
                textWrap: 'nowrap',
              }}
            >
              {headline}
            </Text>
          )}
          {copy && (
            <Text
              color="typography.lighter"
              textStyle={{ base: 'caption', lg: 'bodyLarge' }}
              mb={4}
            >
              {copy}
            </Text>
          )}

          {cta?.text && (
            <Button as={Link} link={cta?.link}>
              {cta.text}
            </Button>
          )}
        </Center>

        <Grid
          templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }}
          templateRows={{ base: 'repeat(3, 7.5rem)', lg: '1fr' }}
          gap={4}
          w={{ base: '100%', lg: '60%' }}
          bottom={{ base: 0, lg: 'unset' }}
          overflow="hidden"
        >
          {chunkedImages.map((chunk, index) => (
            <ImageReel
              key={chunk[0]._key}
              images={chunk}
              initialImageIndex={index * 3}
              inverseAnimation={index % 2 === 1}
            />
          ))}
        </Grid>
      </Flex>
    </Container>
  );
}

CommunityGrid.propTypes = {
  block: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    copy: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }).isRequired,
        alt: PropTypes.string.isRequired,
      })
    ).isRequired,
    cta: PropTypes.shape({
      text: PropTypes.string.isRequired,
      link: PropTypes.shape({
        linkType: PropTypes.string,
        href: PropTypes.string,
        internalLink: PropTypes.shape({}),
      }),
    }),
  }).isRequired,
};
